import React, { useState, useEffect, useRef } from 'react'
import { X, Settings, Lock, Eye, EyeOff, Save, RefreshCw, Users, Shield } from 'lucide-react'

// Declare Turnstile types
declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: any) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface AdminPanelProps {
  onClose: () => void
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  const [adminPassword, setAdminPassword] = useState('')
  const [showAdminPassword, setShowAdminPassword] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [newStaffPassword, setNewStaffPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [newAdminPassword, setNewAdminPassword] = useState('')
  const [showNewAdminPassword, setShowNewAdminPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const turnstileRef = useRef<HTMLDivElement>(null)
  const turnstileWidgetId = useRef<string | null>(null)

  // Get admin password from localStorage or use default
  const getAdminPassword = () => {
    return localStorage.getItem('grace-admin-password') || 'admin2024'
  }

  // Initialize Turnstile for admin panel
  useEffect(() => {
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAAB8F4nMruyyKRcpI',
          callback: (token: string) => {
            setTurnstileToken(token)
          },
          'error-callback': () => {
            setTurnstileToken('')
            setError('Turnstile verification failed. Please try again.')
          },
          'expired-callback': () => {
            setTurnstileToken('')
          }
        })
      }
    }

    // Wait for Turnstile to load
    if (window.turnstile) {
      initTurnstile()
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          initTurnstile()
          clearInterval(checkTurnstile)
        }
      }, 100)

      return () => clearInterval(checkTurnstile)
    }

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current)
      }
    }
  }, [])

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Check Turnstile verification
    if (!turnstileToken) {
      setError('Please complete the security verification.')
      return
    }

    if (adminPassword === getAdminPassword()) {
      setIsAuthenticated(true)
      setError('')
    } else {
      setError('Incorrect admin password')
      setAdminPassword('')
      // Reset Turnstile on failed login
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current)
        setTurnstileToken('')
      }
    }
  }

  const handleChangeStaffPassword = () => {
    if (!newStaffPassword || newStaffPassword.length < 4) {
      setError('Password must be at least 4 characters long')
      return
    }

    // Update the staff password in localStorage
    localStorage.setItem('grace-staff-password', newStaffPassword)
    
    // Clear all existing sessions to force re-login
    localStorage.removeItem('grace-staff-authenticated')
    
    setSuccess('Staff password updated successfully. All users will need to log in again.')
    setNewStaffPassword('')
    setError('')
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleChangeAdminPassword = () => {
    if (!newAdminPassword || newAdminPassword.length < 4) {
      setError('Admin password must be at least 4 characters long')
      return
    }

    // Update the admin password in localStorage
    localStorage.setItem('grace-admin-password', newAdminPassword)
    
    setSuccess('Admin password updated successfully. You will need to use the new password next time.')
    setNewAdminPassword('')
    setError('')
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleResetAllSessions = () => {
    localStorage.removeItem('grace-staff-authenticated')
    setSuccess('All user sessions have been cleared. Users will need to log in again.')
    setError('')
    
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleClearAllData = () => {
    if (confirm('This will clear ALL data including staff directory, announcements, and notes. This action cannot be undone. Are you sure?')) {
      // Clear all localStorage data
      localStorage.clear()
      setSuccess('All data has been cleared. The page will reload.')
      setError('')
      
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-grace-blue-600" />
              Admin Access
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Password
              </label>
              <div className="relative">
                <input
                  type={showAdminPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAdminPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Turnstile Widget */}
            <div className="flex justify-center">
              <div ref={turnstileRef}></div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full btn-primary"
            >
              <Lock className="w-4 h-4 mr-2" />
              Access Admin Panel
            </button>
          </form>

        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-grace-blue-600" />
            Admin Panel
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Password Management */}
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Password Management
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Staff Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newStaffPassword}
                      onChange={(e) => setNewStaffPassword(e.target.value)}
                      placeholder="Enter new staff password"
                      className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 4 characters. This will log out all current users.
                  </p>
                </div>
                
                <button
                  onClick={handleChangeStaffPassword}
                  className="btn-primary flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Update Staff Password
                </button>

                {/* Admin Password Section */}
                <div className="pt-4 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Admin Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewAdminPassword ? 'text' : 'password'}
                        value={newAdminPassword}
                        onChange={(e) => setNewAdminPassword(e.target.value)}
                        placeholder="Enter new admin password"
                        className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewAdminPassword(!showNewAdminPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewAdminPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum 4 characters. You'll need to use this password next time you access admin panel.
                    </p>
                  </div>
                  
                  <button
                    onClick={handleChangeAdminPassword}
                    className="mt-3 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Update Admin Password
                  </button>
                </div>
              </div>
            </div>

            {/* Session Management */}
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Session Management
              </h4>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Force all users to log in again by clearing their sessions.
                </p>
                
                <button
                  onClick={handleResetAllSessions}
                  className="btn-secondary flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Clear All User Sessions
                </button>
              </div>
            </div>

            {/* Data Management */}
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Data Management
              </h4>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Clear all stored data including staff directory, announcements, and notes.
                </p>
                
                <button
                  onClick={handleClearAllData}
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Clear All Data
                </button>
              </div>
            </div>

            {/* System Info */}
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                System Information
              </h4>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Current Staff Password:</span>
                  <span className="font-mono">
                    {localStorage.getItem('grace-staff-password') || 'grace2025'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Admin Password:</span>
                  <span className="font-mono">{getAdminPassword()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Sessions:</span>
                  <span>
                    {localStorage.getItem('grace-staff-authenticated') ? '1' : '0'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Last Updated:</span>
                  <span>{new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminPanel 